import type { QueryClient } from "@tanstack/react-query";
import { api } from "./api";

// Route chunk importers. App.tsx wraps these in React.lazy(); AppShell
// invokes them on nav hover/focus so the chunk is warm by the time the
// user clicks. Calling import() twice resolves the same module promise,
// so this is a free latency win — no double-fetch.
export const routeImporters = {
  dashboard: () => import("@/pages/Dashboard"),
  clients: () => import("@/pages/Clients"),
  clientDetail: () => import("@/pages/ClientDetail"),
  calendar: () => import("@/pages/Calendar"),
  history: () => import("@/pages/History"),
  scheduled: () => import("@/pages/ScheduledCalls"),
  notifications: () => import("@/pages/Notifications"),
  profile: () => import("@/pages/Profile"),
  settings: () => import("@/pages/Settings"),
} as const;

export type RouteName = keyof typeof routeImporters;

const warmed = new Set<RouteName>();

export function preloadRoute(name: RouteName): void {
  if (warmed.has(name)) return;
  warmed.add(name);
  void routeImporters[name]();
}

// Per-route data prefetches. Run alongside chunk preloads so by the time
// the route mounts, both code and data are ready and the page is instant.
export function prefetchRouteData(name: RouteName, qc: QueryClient): void {
  if (name === "dashboard") {
    void qc.prefetchQuery({
      queryKey: ["dashboard"],
      queryFn: () => api("/api/dashboard"),
      staleTime: 15_000,
    });
  } else if (name === "calendar") {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const from = new Date(startOfToday);
    from.setDate(from.getDate() - 7);
    const to = new Date(startOfToday);
    to.setDate(to.getDate() + 60);
    void qc.prefetchQuery({
      queryKey: ["calendar", from.toISOString(), to.toISOString()],
      queryFn: () =>
        api(
          `/api/calendar/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
        ),
      staleTime: 30_000,
    });
  } else if (name === "history") {
    void qc.prefetchQuery({
      queryKey: ["calls", { page: 1 }],
      queryFn: () => api("/api/calls?page=1"),
      staleTime: 30_000,
    });
  } else if (name === "scheduled") {
    void qc.prefetchQuery({
      queryKey: ["scheduled-calls"],
      queryFn: () => api("/api/calls/scheduled"),
      staleTime: 15_000,
    });
  } else if (name === "notifications") {
    void qc.prefetchQuery({
      queryKey: ["notifications"],
      queryFn: () => api("/api/notifications"),
      staleTime: 10_000,
    });
  }
}
